import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

/**
 * Dialog for confirming large exports.
 */
export const ExportConfirmationDialog: React.FC<ExportConfirmationDialogProps> = ({
  open,
  onCancel,
  onConfirm,
  rowCount,
  title = "Large Export Warning",
  message = `You are about to export ${rowCount} rows. This may take some time.`,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="export-confirmation-dialog-title"
      aria-describedby="export-confirmation-dialog-description"
    >
      <DialogTitle id="export-confirmation-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="export-confirmation-dialog-description">{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={onConfirm} color="primary" variant="contained">
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Dialog for displaying export progress.
 */
export const ExportProgressDialog: React.FC<ExportProgressDialogProps> = ({
  open,
  onCancel,
  current,
  total,
  isComplete = false,
  title = "Exporting Data",
  message = "Preparing your export, please wait...",
}) => {
  const progress = (current / total) * 100;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="export-progress-dialog-title"
      aria-describedby="export-progress-dialog-description"
    >
      <DialogTitle id="export-progress-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="export-progress-dialog-description">{message}</DialogContentText>
        <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
          <Box sx={{ width: "100%", mr: 1 }}>
            <div
              style={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "#e0e0e0",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: `${progress}%`,
                  backgroundColor: "#1976d2",
                  borderRadius: 5,
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </Box>
          <Box sx={{ minWidth: 35 }}>
            <Typography variant="body2" color="text.secondary">{`${Math.round(progress)}%`}</Typography>
          </Box>
        </Box>
        <Typography variant="caption" display="block" sx={{ mt: 1, textAlign: "center" }}>
          {`${current} of ${total} rows processed`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary" disabled={isComplete}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Dialog for displaying export errors.
 */
export const ExportErrorDialog: React.FC<ExportErrorDialogProps> = ({
  open,
  onClose,
  error,
  title = "Export Failed",
  messagePrefix = "An error occurred during export:",
}) => {
  let errorMessage = "";
  if (error instanceof Error) {
    errorMessage = error.message;
  } else {
    errorMessage = String(error);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="export-error-dialog-title"
      aria-describedby="export-error-dialog-description"
    >
      <DialogTitle id="export-error-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="export-error-dialog-description">
          {messagePrefix} {errorMessage}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Dialog for displaying a message when the maximum export limit is exceeded.
 */
export const ExportLimitDialog: React.FC<ExportLimitDialogProps> = ({
  open,
  onClose,
  rowCount,
  maxLimit = 10000,
  title = "Export Too Large",
  message = `Your export contains ${rowCount} rows, which exceeds the maximum limit of ${maxLimit} rows. Please narrow your filter criteria and try again.`,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="export-limit-dialog-title"
      aria-describedby="export-limit-dialog-description"
    >
      <DialogTitle id="export-limit-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="export-limit-dialog-description">{message}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Dialog for displaying a loading indicator while determining export size.
 */
export const ExportLoadingDialog: React.FC<ExportLoadingDialogProps> = ({
  open,
  onCancel,
  title = "Preparing Export",
  message = "Determining export size...",
}) => {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="export-loading-dialog-title"
      aria-describedby="export-loading-dialog-description"
    >
      <DialogTitle id="export-loading-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText id="export-loading-dialog-description">{message}</DialogContentText>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "3px solid #f3f3f3",
              borderTop: "3px solid #1976d2",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export interface ExportConfirmationDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Function to handle dialog close without confirmation
   */
  onCancel: () => void;

  /**
   * Function to handle confirmed export
   */
  onConfirm: () => void;

  /**
   * Total number of rows to be exported
   */
  rowCount: number;

  /**
   * Custom title for the dialog
   * @default "Large Export Warning"
   */
  title?: string;

  /**
   * Custom message for the dialog
   * @default "You are about to export {rowCount} rows. This may take some time."
   */
  message?: string;
}

export interface ExportProgressDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Function to handle dialog close/cancel export
   */
  onCancel: () => void;

  /**
   * Number of rows processed so far
   */
  current: number;

  /**
   * Total number of rows to process
   */
  total: number;

  /**
   * Whether the export is complete
   */
  isComplete?: boolean;

  /**
   * Custom title for the dialog
   * @default "Exporting Data"
   */
  title?: string;

  /**
   * Custom message for the dialog
   * @default "Preparing your export, please wait..."
   */
  message?: string;
}

export interface ExportErrorDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Function to handle dialog close
   */
  onClose: () => void;

  /**
   * Error message or object
   */
  error: unknown;

  /**
   * Custom title for the dialog
   * @default "Export Failed"
   */
  title?: string;

  /**
   * Custom message prefix for the dialog
   * @default "An error occurred during export:"
   */
  messagePrefix?: string;
}

export interface ExportLimitDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Function to handle dialog close
   */
  onClose: () => void;

  /**
   * Number of rows that would be exported
   */
  rowCount: number;

  /**
   * Maximum number of rows allowed
   */
  maxLimit?: number;

  /**
   * Custom title for the dialog
   * @default "Export Too Large"
   */
  title?: string;

  /**
   * Custom message for the dialog
   * @default "Your export contains {rowCount} rows, which exceeds the maximum limit of {maxLimit} rows. Please narrow your filter criteria and try again."
   */
  message?: string;
}

export interface ExportLoadingDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Function to handle dialog close/cancel
   */
  onCancel: () => void;

  /**
   * Custom title for the dialog
   * @default "Preparing Export"
   */
  title?: string;

  /**
   * Custom message for the dialog
   * @default "Determining export size..."
   */
  message?: string;
}
